import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  X, Loader2, Eye, MousePointerClick, ToggleRight, Tag, Download, PenTool, CreditCard, Activity,
} from "lucide-react";
import { api } from "../lib/api";
import { euro, fmtDate, fmtDateTime } from "../lib/format";

const ICONS = {
  APERTURA: { icon: Eye, cls: "bg-violet-100 text-violet-600" },
  CAMBIO_TIER: { icon: MousePointerClick, cls: "bg-indigo-100 text-indigo-600" },
  TOGGLE_ADDON: { icon: ToggleRight, cls: "bg-sky-100 text-sky-600" },
  CODICE_PROMO: { icon: Tag, cls: "bg-emerald-100 text-emerald-600" },
  DOWNLOAD_PDF: { icon: Download, cls: "bg-slate-200 text-slate-600" },
  FIRMA: { icon: PenTool, cls: "bg-emerald-100 text-emerald-700" },
  PAGAMENTO: { icon: CreditCard, cls: "bg-orange-100 text-orange-600" },
};

function MiniCard({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</p>
      <p className="text-sm font-bold text-slate-900 mt-0.5 truncate">{value}</p>
    </div>
  );
}

export default function ActivityLog({ quote, onClose }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!quote) return;
    setData(null);
    api.get(`/quotes/${quote.id}/activity`).then((r) => setData(r.data));
  }, [quote]);

  if (!quote) return null;

  return createPortal(
    <div className="fixed inset-0 z-50" data-testid="activity-log-panel">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-card shadow-2xl flex flex-col animate-in slide-in-from-right duration-300">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-primary" />
            <div>
              <h2 className="font-bold text-slate-900 leading-tight">Registro Attività</h2>
              <p className="text-xs text-slate-400 font-mono">{quote.quote_number}</p>
            </div>
          </div>
          <button data-testid="activity-log-close" onClick={onClose} className="text-slate-400 hover:text-slate-900">
            <X className="w-5 h-5" />
          </button>
        </div>

        {!data ? (
          <div className="flex-1 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : (
          <div className="flex-1 overflow-y-auto p-5">
            <div className="grid grid-cols-2 gap-3 mb-6">
              <MiniCard label="Totale Visite" value={data.totale_visite || 0} />
              <MiniCard label="Prima Apertura" value={fmtDate(data.prima_visita)} />
              <MiniCard label="Ultima Attività" value={fmtDate(data.ultima_visita)} />
              <MiniCard label="Ultimo Importo" value={data.last_amount != null ? euro(data.last_amount) : "—"} />
            </div>

            {data.logs.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-10">Nessuna attività registrata.</p>
            ) : (
              <div className="relative pl-6">
                <div className="absolute left-[11px] top-1 bottom-1 w-px bg-slate-200" />
                {data.logs.map((l) => {
                  const meta = ICONS[l.type] || ICONS.APERTURA;
                  const Icon = meta.icon;
                  return (
                    <div key={l.id} data-testid={`activity-event-${l.type}`} className="relative mb-5 last:mb-0">
                      <div className={`absolute -left-6 w-6 h-6 rounded-full flex items-center justify-center ${meta.cls}`}>
                        <Icon style={{ width: 13, height: 13 }} />
                      </div>
                      <p className="text-sm text-slate-800 leading-snug ml-1">{l.message}</p>
                      <p className="text-xs text-slate-400 ml-1 mt-0.5">{fmtDateTime(l.created_at)}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
